import React from 'react';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
                alt="Systemklar" 
                className="h-8"
              />
            </Link>
            <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700">
              Logg inn
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Personvernerklæring</h1>
          <p className="text-gray-500 mb-4">Sist oppdatert: April 2026</p>
          
          {/* GDPR intro - prominent placement */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
            <p className="text-blue-800 font-medium">
              Vi behandler personopplysninger i samsvar med personopplysningsloven og EUs personvernforordning (GDPR).
            </p>
          </div>

          <div className="prose prose-gray max-w-none">
            {/* 1. Innledning */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Innledning</h2>
              <p className="text-gray-700 mb-4">
                Denne personvernerklæringen beskriver hvordan Valoria Solutions AS («vi», «oss» eller «Selskapet») 
                samler inn, bruker, lagrer og beskytter dine personopplysninger når du bruker Systemklar-plattformen 
                («Tjenesten»).
              </p>
              <p className="text-gray-700">
                Vi er forpliktet til å beskytte ditt personvern i henhold til EUs personvernforordning (GDPR) 
                og norsk personopplysningslov.
              </p>
            </section>

            {/* 2. Behandlingsansvarlig */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Behandlingsansvarlig</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700"><strong>Valoria Solutions AS</strong></p>
                <p className="text-gray-700">E-post: post@valoria.no</p>
              </div>
            </section>

            {/* 3. Rettslig grunnlag - GDPR intro */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Rettslig grunnlag</h2>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                <p className="text-blue-800">
                  Vi behandler personopplysninger i samsvar med EUs personvernforordning (GDPR) og 
                  gjeldende norsk personvernlovgivning (personopplysningsloven).
                </p>
              </div>
              <p className="text-gray-700">
                All behandling av personopplysninger har et gyldig rettslig grunnlag, som beskrevet 
                i oversikten nedenfor.
              </p>
            </section>

            {/* 4. Hvilke opplysninger samler vi inn */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Hvilke opplysninger samler vi inn</h2>
              
              <h3 className="font-semibold text-gray-800 mb-2 mt-4">4.1 Opplysninger du gir oss</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li><strong>Kontaktinformasjon:</strong> Navn, e-postadresse, telefonnummer</li>
                <li><strong>Bedriftsinformasjon:</strong> Organisasjonsnummer, firmanavn, bransje, adresse, antall ansatte</li>
                <li><strong>Kontoinformasjon:</strong> Brukernavn, passord (kryptert), stillingstittel</li>
                <li><strong>Innhold du oppretter:</strong> Oppgaver, avvik, risikovurderinger, dokumenter og annet innhold</li>
                <li><strong>Brukerinput til AI:</strong> Informasjon du oppgir for å generere innhold</li>
              </ul>

              <h3 className="font-semibold text-gray-800 mb-2 mt-4">4.2 Opplysninger vi samler automatisk</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li><strong>Teknisk informasjon:</strong> IP-adresse, nettlesertype, enhet, operativsystem</li>
                <li><strong>Bruksdata:</strong> Hvilke funksjoner du bruker, tidspunkter for innlogging, aktivitetslogger</li>
                <li><strong>Informasjonskapsler (cookies):</strong> Se punkt 8</li>
              </ul>
            </section>

            {/* 5. Formål med behandlingen */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Formål med behandlingen</h2>
              <p className="text-gray-700 mb-4">Vi behandler dine personopplysninger for følgende formål:</p>
              
              <table className="w-full border-collapse border border-gray-300 mb-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left">Formål</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Rettslig grunnlag</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Levere og administrere Tjenesten</td>
                    <td className="border border-gray-300 px-4 py-2">Oppfyllelse av avtale</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Kundesupport og kommunikasjon</td>
                    <td className="border border-gray-300 px-4 py-2">Oppfyllelse av avtale</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Fakturering og betalingshåndtering</td>
                    <td className="border border-gray-300 px-4 py-2">Oppfyllelse av avtale</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Forbedre og videreutvikle Tjenesten</td>
                    <td className="border border-gray-300 px-4 py-2">Berettiget interesse</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Sende viktige oppdateringer og varsler</td>
                    <td className="border border-gray-300 px-4 py-2">Oppfyllelse av avtale</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Sende markedsføring (med samtykke)</td>
                    <td className="border border-gray-300 px-4 py-2">Samtykke</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Oppfylle lovpålagte krav</td>
                    <td className="border border-gray-300 px-4 py-2">Rettslig forpliktelse</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* 6. Deling med tredjeparter */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Deling med tredjeparter</h2>
              <p className="text-gray-700 mb-4">
                Vi deler personopplysninger med følgende kategorier av tredjeparter:
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-6">
                <h3 className="font-semibold text-blue-800 mb-2">6.1 AI-leverandører (kunstig intelligens)</h3>
                <p className="text-blue-700 mb-3">
                  For å generere innhold i Tjenesten benytter vi tredjeparts AI-tjenester.
                </p>
                <div className="bg-white/50 rounded p-3 mb-3">
                  <p className="text-blue-800 font-medium mb-2">Behandling av brukerinput:</p>
                  <ul className="list-disc pl-6 text-blue-700 space-y-2">
                    <li>
                      Brukerinput kan bli behandlet av tredjeparts AI-leverandører (f.eks. OpenAI) 
                      for å generere innhold.
                    </li>
                    <li>
                      Slik behandling er regulert av databehandleravtaler og leverandørenes egne vilkår.
                    </li>
                    <li>
                      AI-leverandører kan behandle data på servere utenfor EU/EØS, men vi sikrer 
                      tilstrekkelige garantier gjennom EU Standard Contractual Clauses.
                    </li>
                  </ul>
                </div>
                <p className="text-blue-700 text-sm">
                  <strong>Anbefaling:</strong> Unngå å oppgi sensitive personopplysninger 
                  (særlige kategorier iht. GDPR art. 9) i AI-input.
                </p>
              </div>

              <h3 className="font-semibold text-gray-800 mb-2 mt-4">6.2 Andre databehandlere</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li><strong>Skyhosting:</strong> For lagring av data og drift av Tjenesten</li>
                <li><strong>Betalingsleverandør:</strong> Stripe for håndtering av betalinger (vi lagrer ikke kortnumre)</li>
                <li><strong>E-posttjenester:</strong> For sending av transaksjonelle e-poster og varsler</li>
                <li><strong>Analyseverktøy:</strong> For å forstå bruksmønstre og forbedre Tjenesten</li>
              </ul>

              <p className="text-gray-700">
                Alle databehandlere er bundet av databehandleravtaler som sikrer at dine opplysninger 
                behandles i henhold til gjeldende personvernlovgivning.
              </p>
            </section>

            {/* 7. Lagring og sletting */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Lagring og sletting</h2>
              
              <h3 className="font-semibold text-gray-800 mb-2">7.1 Lagringssted</h3>
              <p className="text-gray-700 mb-4">
                Dine data lagres primært på servere innenfor EU/EØS-området. Ved overføring til 
                tredjeland (f.eks. USA for AI-tjenester) sikrer vi at det foreligger tilstrekkelige 
                garantier, som EU Standard Contractual Clauses.
              </p>

              <h3 className="font-semibold text-gray-800 mb-2">7.2 Lagringstid</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li><strong>Aktive kontoer:</strong> Data lagres så lenge kontoen er aktiv</li>
                <li><strong>Inaktive kontoer:</strong> Data slettes 24 måneder etter siste innlogging</li>
                <li><strong>Etter oppsigelse:</strong> Data slettes innen 90 dager, med mindre lovgivning krever lengre lagring</li>
                <li><strong>Regnskapsmateriale:</strong> Lagres i 5 år iht. bokføringsloven</li>
              </ul>

              <h3 className="font-semibold text-gray-800 mb-2">7.3 Sletting</h3>
              <p className="text-gray-700">
                Du kan be om sletting av din konto og tilhørende data. Ved sletting fjernes dine 
                personopplysninger, med unntak av data vi er lovpålagt å oppbevare.
              </p>
            </section>

            {/* 8. Informasjonskapsler */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Informasjonskapsler (cookies)</h2>
              <p className="text-gray-700 mb-4">
                Vi bruker følgende typer informasjonskapsler:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li><strong>Nødvendige:</strong> For at Tjenesten skal fungere (f.eks. innlogging)</li>
                <li><strong>Funksjonelle:</strong> For å huske dine preferanser (f.eks. språkvalg)</li>
                <li><strong>Analyse:</strong> For å forstå hvordan Tjenesten brukes</li>
              </ul>
              <p className="text-gray-700">
                Du kan administrere cookies via nettleserens innstillinger.
              </p>
            </section>

            {/* 9. Dine rettigheter */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Dine rettigheter (GDPR)</h2>
              <p className="text-gray-700 mb-4">
                Du har følgende rettigheter knyttet til dine personopplysninger:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-1">Innsyn</h4>
                  <p className="text-gray-600 text-sm">Rett til å få vite hvilke opplysninger vi har om deg</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-1">Retting</h4>
                  <p className="text-gray-600 text-sm">Rett til å få korrigert feilaktige opplysninger</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-1">Sletting</h4>
                  <p className="text-gray-600 text-sm">Rett til å få slettet dine opplysninger</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-1">Dataportabilitet</h4>
                  <p className="text-gray-600 text-sm">Rett til å få utlevert dine data i maskinlesbart format</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-1">Begrensning</h4>
                  <p className="text-gray-600 text-sm">Rett til å begrense behandlingen i visse situasjoner</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-1">Innsigelse</h4>
                  <p className="text-gray-600 text-sm">Rett til å protestere mot behandling basert på berettiget interesse</p>
                </div>
              </div>

              <p className="text-gray-700">
                For å utøve dine rettigheter, kontakt oss på <strong>personvern@systemklar.no</strong>. 
                Vi vil besvare henvendelser innen 30 dager.
              </p>
            </section>

            {/* 10. Sikkerhet */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Sikkerhet</h2>
              <p className="text-gray-700 mb-4">
                Vi tar datasikkerhet på alvor og har implementert følgende tiltak:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>Kryptering av data under overføring (HTTPS/TLS)</li>
                <li>Kryptert lagring av passord (bcrypt)</li>
                <li>Tilgangskontroll og logging</li>
                <li>Regelmessige sikkerhetsgjennomganger</li>
                <li>Varsling ved sikkerhetsbrudd (iht. GDPR)</li>
              </ul>
            </section>

            {/* 11. Klage */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Klage til Datatilsynet</h2>
              <p className="text-gray-700 mb-4">
                Dersom du mener at vi behandler dine personopplysninger i strid med personvernlovgivningen, 
                har du rett til å klage til Datatilsynet:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700"><strong>Datatilsynet</strong></p>
                <p className="text-gray-700">Postboks 458 Sentrum, 0105 Oslo</p>
                <p className="text-gray-700">E-post: postkasse@datatilsynet.no</p>
                <p className="text-gray-700">www.datatilsynet.no</p>
              </div>
            </section>

            {/* 12. Endringer */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Endringer i personvernerklæringen</h2>
              <p className="text-gray-700">
                Vi kan oppdatere denne personvernerklæringen fra tid til annen. Ved vesentlige endringer 
                vil vi varsle deg via e-post eller i Tjenesten. Den til enhver tid gjeldende versjonen 
                er tilgjengelig på denne siden.
              </p>
            </section>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-center gap-6 text-sm">
          <Link to="/terms" className="text-blue-600 hover:text-blue-700">
            Vilkår for bruk
          </Link>
          <Link to="/disclaimer" className="text-blue-600 hover:text-blue-700">
            Ansvarsfraskrivelse
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-500">
          © 2025 Valoria Solutions AS. Alle rettigheter reservert.
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
